import React, { useState, useEffect } from 'react';
import { useSearchParams, Navigate, Link } from 'react-router-dom';
import { 
  Container, Box, Typography, Paper, CircularProgress, 
  Alert, Button 
} from '@mui/material';
import { CheckCircle, Error, Email } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const EmailVerification = () => {
  const [searchParams] = useSearchParams();
  const { verifyEmail, loading } = useAuth();
  
  const [verificationStatus, setVerificationStatus] = useState('verifying'); // 'verifying', 'success', 'failed'
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isExpired, setIsExpired] = useState(false);
  
  // Get token and email from URL parameters
  const token = searchParams.get('token');
  const email = searchParams.get('email');
  
  useEffect(() => {
    const performVerification = async () => {
      if (!token || !email) {
        setVerificationStatus('failed');
        setError('Invalid verification link. Token or email is missing.');
        return;
      }
      
      try {
        const result = await verifyEmail(token, email);
        
        if (result.success) {
          setVerificationStatus('success');
          setMessage(result.message);
        } else {
          setVerificationStatus('failed');
          setError(result.error || 'Verification failed');
          setIsExpired(result.expired || false);
        }
      } catch (err) {
        setVerificationStatus('failed');
        setError('An unexpected error occurred during verification');
      }
    };
    
    performVerification();
  }, [token, email, verifyEmail]);
  
  // Don't redirect if already authenticated - allow logged-in users to verify their email
  const { isAuthenticated, currentUser } = useAuth();
  
  const renderContent = () => {
    switch (verificationStatus) {
      case 'verifying':
        return (
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={64} sx={{ mb: 3, color: 'primary.main' }} />
            <Typography variant="h5" gutterBottom>
              Verifying Your Email
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Please wait while we verify your email address...
            </Typography>
          </Box>
        );
        
      case 'success':
        return (
          <Box sx={{ textAlign: 'center' }}>
            <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom color="success.main">
              Email Verified!
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              {message || 'Your email has been successfully verified.'}
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              You can now log in to your account and start using the app.
            </Typography>
            <Button
              component={Link}
                              to={isAuthenticated ? "/" : "/"}
              variant="contained"
              size="large"
              sx={{ mt: 2 }}
            >
              {isAuthenticated ? "Continue to Dashboard" : "Sign In"}
            </Button>
          </Box>
        );
        
      case 'failed':
        return (
          <Box sx={{ textAlign: 'center' }}>
            <Error sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom color="error.main">
              Verification Failed
            </Typography>
            
            <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
              {error}
            </Alert>
            
            {isExpired && (
              <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
                Your verification link has expired. You can request a new verification email from the login page.
              </Alert>
            )}
            
            <Box sx={{ mt: 3 }}>
              <Button
                component={Link}
                to={isAuthenticated ? "/" : "/"}
                variant="contained"
                sx={{ mr: 2 }}
              >
                {isAuthenticated ? "Go to Dashboard" : "Go to Login"}
              </Button>
              
              {isExpired && (
                <Button
                  component={Link}
                  to={isAuthenticated ? "/settings" : "/verify-email-pending"}
                  state={{ email }}
                  variant="outlined"
                >
                  {isAuthenticated ? "Manage Email Settings" : "Request New Verification"}
                </Button>
              )}
            </Box>
          </Box>
        );
        
      default:
        return null;
    }
  };
  
  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          {renderContent()}
        </Paper>
        
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Need help? Contact support or try logging in again.
          </Typography>
        </Box>
      </Box>
    </Container>
  );
};

export default EmailVerification; 